import { useState } from "react";
import { X, Trash2, Minus, Plus, MessageCircle, Mail } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";
import CheckoutDialog from "./CheckoutDialog";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, total, count, clear } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  if (!open) return null;

  return (
    <>
      <div
        data-testid="cart-overlay"
        className="fixed inset-0 bg-stone-900/50 z-50"
        onClick={() => setOpen(false)}
      />
      <aside
        data-testid="cart-drawer"
        className="fixed right-0 top-0 bottom-0 w-full sm:w-[440px] bg-stone-50 z-50 shadow-2xl flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="font-serif text-2xl text-stone-900">
            Panier <span className="text-stone-400 text-base">({count})</span>
          </h2>
          <button
            data-testid="close-cart-button"
            onClick={() => setOpen(false)}
            className="p-2 hover:bg-stone-200 rounded-sm transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-stone-500 font-serif text-xl">Votre panier est vide.</p>
              <button
                onClick={() => setOpen(false)}
                className="mt-4 px-4 py-2 text-sm uppercase tracking-wider text-orange-800 hover:text-orange-900"
              >
                Continuer mes achats
              </button>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                data-testid={`cart-item-${item.id}`}
                className="flex gap-4 bg-white border border-stone-200 p-3 rounded-sm"
              >
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-20 h-20 object-cover bg-stone-100 rounded-sm"
                />
                <div className="flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-medium text-stone-900 line-clamp-2 text-sm">{item.name}</h3>
                    <button
                      data-testid={`remove-cart-${item.id}`}
                      onClick={() => removeItem(item.id)}
                      className="text-stone-400 hover:text-red-700 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center border border-stone-300 rounded-sm">
                      <button
                        data-testid={`qty-minus-${item.id}`}
                        onClick={() => updateQty(item.id, item.qty - 1)}
                        className="px-2 py-1 hover:bg-stone-100"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="px-3 text-sm font-medium">{item.qty}</span>
                      <button
                        data-testid={`qty-plus-${item.id}`}
                        onClick={() => updateQty(item.id, item.qty + 1)}
                        className="px-2 py-1 hover:bg-stone-100"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <span className="font-black text-orange-700">{formatPrice(item.price * item.qty)}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-stone-200 p-6 space-y-4 bg-white">
            <div className="flex items-center justify-between">
              <span className="uppercase tracking-[0.15em] text-xs font-semibold text-stone-500">Total</span>
              <span data-testid="cart-total" className="text-3xl font-black text-orange-700">
                {formatPrice(total)}
              </span>
            </div>
            <button
              data-testid="checkout-button"
              onClick={() => setCheckoutOpen(true)}
              className="w-full py-3 bg-orange-800 hover:bg-orange-900 text-white font-medium rounded-sm transition-colors inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={18} />
              Finaliser la commande
            </button>
            <button
              onClick={clear}
              data-testid="clear-cart-button"
              className="w-full text-xs uppercase tracking-[0.15em] text-stone-500 hover:text-stone-900"
            >
              Vider le panier
            </button>
          </div>
        )}
      </aside>

      {checkoutOpen && <CheckoutDialog onClose={() => setCheckoutOpen(false)} />}
    </>
  );
}
